import { useQueryExamAll } from "@/api/exam/exam";
import { useQueryCertificateAll } from "@/api/certificate/certificate";
import { RegistrationType } from "@/api/enums/RegistrationType";
import { ExamType } from "@/api/enums/ExamType";
import { useMemo } from "react";

const examTypeByRegistrationType: Record<RegistrationType, ExamType[]> = {
  [RegistrationType.INITIAL]: [ExamType.INITIAL],
  [RegistrationType.EXTENSION]: [ExamType.INITIAL],
  [RegistrationType.RENEW]: [ExamType.RENEW],
};

interface UseAvailableExamsProps {
  registrationType?: RegistrationType;
  candidateId?: number;
}

export const useAvailableExams = ({
  registrationType,
  candidateId,
}: UseAvailableExamsProps) => {
  const {
    data: exams,
    isLoading: isLoadingExams,
    isError: isErrorExams,
  } = useQueryExamAll();
  const { data: certificates, isLoading: isLoadingCerts } =
    useQueryCertificateAll(candidateId);

  const filteredExams = useMemo(() => {
    if (!exams?.length) return [];

    const now = new Date();

    // First filter: always check registration end date
    let registrableExams = exams.filter(
      (exam) => new Date(exam.registeringEndDate) >= now
    );

    // Second filter: check exam type based on registration type
    if (registrationType) {
      const allowedTypes = examTypeByRegistrationType[registrationType];
      registrableExams = registrableExams.filter((exam) =>
        allowedTypes.includes(exam.type)
      );
    }

    // If not renewal, return filtered exams
    if (registrationType !== RegistrationType.RENEW) return registrableExams;

    // For renewals, we need certificates
    if (!certificates?.length) return [];

    const isWithin12MonthsFromBaseDate = (date: Date, baseDate: Date) => {
      const twelveMonthsBefore = new Date(baseDate);
      twelveMonthsBefore.setMonth(twelveMonthsBefore.getMonth() - 12);
      return date >= twelveMonthsBefore && date <= baseDate;
    };

    // Find certificates that are still valid
    const validCertificates = certificates.filter((certificate) => {
      const expirationDate = new Date(certificate.expirationDate);
      return expirationDate >= now;
    });

    if (validCertificates.length === 0) return [];

    // For each registrable exam, check if it's before any of the valid certificates and within 12 months of the certificate expiration date
    return registrableExams.filter((exam) => {
      const examDate = new Date(exam.date);
      return validCertificates.some(
        (certificate) =>
          examDate <= new Date(certificate.expirationDate) &&
          isWithin12MonthsFromBaseDate(
            examDate,
            new Date(certificate.expirationDate)
          )
      );
    });
  }, [exams, certificates, registrationType]);

  return {
    exams: filteredExams,
    isLoading: isLoadingExams || isLoadingCerts,
    isError: isErrorExams,
  };
};
